import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Trophy, Plus, Eye, Edit, Trash2, Users, Calendar, Clock,
  Globe, Flag, GraduationCap, Award, Zap, BookOpen, Target,
  ArrowRight, CheckCircle, AlertCircle, Loader2, ChevronRight,
  DollarSign, Building2, Settings, BarChart3, FileText, Crown,
  Play, Pause, RefreshCw, ExternalLink, Copy, Check, Share2,
  Linkedin, Twitter, Send, X, Tag, Percent, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// Challenge type options
const challengeTypes = [
  { id: 'national', name: 'National Level Challenge', icon: Flag },
  { id: 'international', name: 'International Competition', icon: Globe },
  { id: 'admission', name: 'Admission Test', icon: GraduationCap },
  { id: 'scholarship', name: 'Scholarship Test', icon: Award },
  { id: 'hackathon', name: 'Hackathon', icon: Zap },
  { id: 'assessment', name: 'Assessment', icon: BookOpen },
  { id: 'olympiad', name: 'Olympiad', icon: Trophy }
];

const CompanyEnterpriseHostingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(localStorage.getItem('company_token'));
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [pricing, setPricing] = useState({});
  const [activeTab, setActiveTab] = useState('challenges');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutChallenge, setCheckoutChallenge] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Status update state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalData, setStatusModalData] = useState({ challenge: null, newStatus: '' });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareChallenge, setShareChallenge] = useState(null);
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    slug: '',
    challenge_type: 'national',
    short_description: '',
    description: '',
    participation_type: 'individual',
    max_team_size: 1,
    registration_start: '',
    registration_end: '',
    challenge_start: '',
    challenge_end: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchData();
    
    // Handle payment callback
    const orderId = searchParams.get('order_id');
    if (orderId) {
      verifyPayment(orderId);
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [challengesRes, pricingRes] = await Promise.all([
        axios.get(`${API}/api/company/enterprise/challenges`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/public/enterprise/pricing`)
      ]);
      setChallenges(challengesRes.data.challenges || []);
      setPricing(pricingRes.data.plans || {});
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (err.response?.status === 401) {
        navigate('/company/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/company/enterprise/verify-payment?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.verified) {
        toast.success('Payment verified! Challenge submitted for approval.');
        fetchData();
      }
    } catch (err) {
      console.error('Payment verification failed:', err);
    }
    // Clear the URL params
    window.history.replaceState({}, '', '/company/enterprise');
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) {
      toast.error('Challenge title is required');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...createForm,
        slug: createForm.slug || createForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        pricing_plan: selectedPlan
      };
      
      const res = await axios.post(`${API}/api/company/enterprise/challenges`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Challenge created! Proceed to payment to publish.');
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        slug: '',
        challenge_type: 'national',
        short_description: '',
        description: '',
        participation_type: 'individual',
        max_team_size: 1,
        registration_start: '',
        registration_end: '',
        challenge_start: '',
        challenge_end: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setCreating(false);
    }
  };

  const handlePurchasePlan = async (challengeId) => {
    // Open checkout modal with the challenge
    const challenge = challenges.find(c => c.id === challengeId);
    if (challenge) {
      setCheckoutChallenge(challenge);
      setCouponCode('');
      setCouponApplied(null);
      setShowCheckoutModal(true);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponValidating(true);
    try {
      const res = await axios.post(`${API}/api/public/enterprise/validate-coupon`, {
        code: couponCode.trim(),
        plan: checkoutChallenge?.pricing_plan || 'starter'
      });
      
      setCouponApplied(res.data);
      toast.success(`Coupon applied! ${res.data.discount_type === 'percentage' ? res.data.discount_value + '%' : '₹' + res.data.discount_value} off`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon code');
      setCouponApplied(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
  };

  const calculateDiscountedPrice = () => {
    if (!checkoutChallenge || !couponApplied) return checkoutChallenge?.plan_details?.price || 49999;
    
    const originalPrice = checkoutChallenge.plan_details?.price || 49999;
    if (couponApplied.discount_type === 'percentage') {
      return Math.max(originalPrice - (originalPrice * couponApplied.discount_value / 100), 0);
    } else {
      return Math.max(originalPrice - couponApplied.discount_value, 0);
    }
  };

  const processPayment = async () => {
    if (!checkoutChallenge) return;
    
    setProcessingPayment(true);
    try {
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${checkoutChallenge.id}/purchase`,
        { coupon_code: couponApplied?.code || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if it's a free checkout (100% discount)
      if (res.data.free_checkout) {
        toast.success(res.data.message || 'Challenge submitted for approval!');
        setShowCheckoutModal(false);
        setCheckoutChallenge(null);
        setCouponCode('');
        setCouponApplied(null);
        fetchData();
        return;
      }
      
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        await cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else {
        toast.error('Payment initialization failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to delete this challenge?')) return;
    
    try {
      await axios.delete(`${API}/api/company/enterprise/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete challenge');
    }
  };

  const copyLink = (challenge, linkType = 'landing') => {
    const baseUrl = getFrontendUrl();
    const ref = '?ref=company_dashboard';
    let url = '';
    
    if (linkType === 'landing') {
      url = `${baseUrl}/enterprise/${challenge.org_slug}/${challenge.slug}${ref}`;
    } else if (linkType === 'assessment') {
      url = `${baseUrl}/enterprise/${challenge.id}/assessment${ref}`;
    }
    
    navigator.clipboard.writeText(url);
    toast.success(`${linkType === 'landing' ? 'Challenge' : 'Assessment'} link copied!`);
  };

  // Status update functions
  const getAvailableStatusActions = (challenge) => {
    const actions = [];
    switch (challenge.status) {
      case 'draft':
        if (challenge.payment_status === 'completed') {
          actions.push({ status: 'pending_approval', label: 'Submit for Approval', icon: Send, color: 'amber' });
        }
        break;
      case 'approved':
        actions.push({ status: 'live', label: 'Go Live', icon: Play, color: 'green' });
        break;
      case 'live':
        actions.push({ status: 'expired', label: 'End Challenge', icon: Pause, color: 'gray' });
        break;
      default:
        break;
    }
    return actions;
  };

  const initiateStatusChange = (challenge, newStatus) => {
    // Critical statuses require confirmation
    if (['live', 'expired'].includes(newStatus)) {
      setStatusModalData({ challenge, newStatus });
      setShowStatusModal(true);
    } else {
      handleUpdateStatus(challenge.id, newStatus);
    }
  };

  const handleUpdateStatus = async (challengeId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Challenge status updated to "${newStatus}"`);
      setShowStatusModal(false);
      setStatusModalData({ challenge: null, newStatus: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Share functions
  const openShareModal = (challenge) => {
    setShareChallenge(challenge);
    setShowShareModal(true);
  };

  const shareOnSocial = (platform) => {
    if (!shareChallenge) return;
    
    const baseUrl = getFrontendUrl();
    const url = encodeURIComponent(`${baseUrl}/enterprise/${shareChallenge.org_slug}/${shareChallenge.slug}?ref=${platform}`);
    const title = encodeURIComponent(shareChallenge.title);
    const text = encodeURIComponent(`Check out "${shareChallenge.title}" - ${shareChallenge.short_description || 'Join now!'}`);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
    
    // Track the share
    axios.post(`${API}/api/enterprise/track-link-click?challenge_id=${shareChallenge.id}&ref=${platform}&link_type=share`).catch(() => {});
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const configs = {
      draft: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'Draft' },
      pending_approval: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Pending Approval' },
      approved: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Approved' },
      live: { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', label: 'Live' },
      completed: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Completed' },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Rejected' }
    };
    const config = configs[status] || configs.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/company/dashboard" className="text-gray-400 hover:text-white">
                <ChevronRight className="rotate-180" size={24} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Crown className="text-amber-400" />
                  Enterprise Challenge Hosting
                </h1>
                <p className="text-sm text-gray-400">Host national & international competitions</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              data-testid="create-challenge-btn"
            >
              <Plus size={20} />
              Host New Challenge
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b border-white/10 bg-[#12121a]/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'challenges', label: 'My Challenges', icon: Trophy },
              { id: 'pricing', label: 'Pricing Plans', icon: DollarSign },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-purple-400 border-purple-400'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="space-y-6">
            {challenges.length === 0 ? (
              <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl">
                <Trophy size={64} className="mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-semibold text-white mb-2">No Challenges Yet</h3>
                <p className="text-gray-400 mb-6">Host your first enterprise challenge and reach thousands of participants.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg font-medium"
                >
                  Create Your First Challenge
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {challenges.map(challenge => (
                  <div
                    key={challenge.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-purple-500/30 transition-all"
                    data-testid={`challenge-card-${challenge.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-white">{challenge.title}</h3>
                          {getStatusBadge(challenge.status)}
                        </div>
                        <p className="text-gray-400 text-sm mb-4">{challenge.short_description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {challenge.registration_count || 0} registrations
                          </span>
                          {challenge.challenge_start && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(challenge.challenge_start).toLocaleDateString()}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Crown size={14} className="text-amber-400" />
                            {challenge.plan_details?.name || 'Starter'} Plan
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        {/* Status Action Buttons */}
                        <div className="flex items-center gap-2">
                          {getAvailableStatusActions(challenge).map(action => (
                            <button
                              key={action.status}
                              onClick={() => initiateStatusChange(challenge, action.status)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                                action.color === 'green' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30' :
                                action.color === 'amber' ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30' :
                                'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border border-gray-500/30'
                              }`}
                              data-testid={`status-action-${challenge.id}-${action.status}`}
                            >
                              <action.icon size={16} />
                              {action.label}
                            </button>
                          ))}
                          
                          {challenge.status === 'draft' && challenge.payment_status !== 'completed' && (
                            <button
                              onClick={() => handlePurchasePlan(challenge.id)}
                              className="px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                              data-testid={`pay-btn-${challenge.id}`}
                            >
                              <DollarSign size={16} />
                              Pay & Publish
                            </button>
                          )}
                        </div>
                        
                        {/* Action Buttons Row */}
                        <div className="flex items-center gap-2 justify-end">
                          {/* Copy/Share Links for approved/live challenges */}
                          {['approved', 'live'].includes(challenge.status) && (
                            <>
                              <button
                                onClick={() => copyLink(challenge, 'landing')}
                                className="p-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-all"
                                title="Copy Challenge Link"
                                data-testid={`copy-link-${challenge.id}`}
                              >
                                <Copy size={16} />
                              </button>
                              <button
                                onClick={() => openShareModal(challenge)}
                                className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-all"
                                title="Share"
                                data-testid={`share-btn-${challenge.id}`}
                              >
                                <Share2 size={16} />
                              </button>
                              <a
                                href={`/enterprise/${challenge.org_slug}/${challenge.slug}?ref=company_dashboard`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 hover:text-white transition-all"
                                title="View Live Page"
                                data-testid={`view-live-${challenge.id}`}
                              >
                                <ExternalLink size={16} />
                              </a>
                            </>
                          )}
                          
                          <Link
                            to={`/company/enterprise/challenges/${challenge.id}`}
                            className="p-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 hover:text-white transition-all"
                            title="Manage Challenge"
                            data-testid={`manage-btn-${challenge.id}`}
                          >
                            <Settings size={16} />
                          </Link>
                          
                          {challenge.status === 'draft' && (
                            <button
                              onClick={() => handleDeleteChallenge(challenge.id)}
                              className="p-2 bg-white/5 text-gray-400 rounded-lg hover:bg-red-500/20 hover:text-red-400"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Choose Your Plan</h2>
              <p className="text-gray-400">Select a plan that fits your challenge scale</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {Object.values(pricing).filter(p => p.enabled).map((plan, idx) => (
                <div
                  key={plan.id}
                  className={`relative bg-white/5 border rounded-2xl p-6 transition-all hover:border-purple-500/50 ${
                    plan.id === 'pro' ? 'border-purple-500/50 ring-2 ring-purple-500/20' : 'border-white/10'
                  }`}
                >
                  {plan.id === 'pro' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full text-white text-sm font-medium">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-white">
                      {formatPrice(plan.price)}
                    </div>
                    <p className="text-gray-500 text-sm mt-1">per challenge</p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features?.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2 text-gray-300 text-sm">
                        <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => {
                      setSelectedPlan(plan.id);
                      setShowCreateModal(true);
                    }}
                    className={`w-full py-3 rounded-xl font-medium transition-all ${
                      plan.id === 'pro'
                        ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-purple-500/25'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Total Challenges</span>
                  <Trophy className="text-purple-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-white">{challenges.length}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Live Challenges</span>
                  <Play className="text-green-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-white">
                  {challenges.filter(c => c.status === 'live').length}
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Total Registrations</span>
                  <Users className="text-cyan-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-white">
                  {challenges.reduce((sum, c) => sum + (c.registration_count || 0), 0)}
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Pending Approval</span>
                  <Clock className="text-amber-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-white">
                  {challenges.filter(c => c.status === 'pending_approval').length}
                </div>
              </div>
            </div>
            
            {challenges.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                <p>Create your first challenge to see analytics</p>
              </div>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Challenges Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Challenge</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Registrations</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {challenges.slice(0, 10).map(challenge => (
                        <tr key={challenge.id} className="border-b border-white/5">
                          <td className="py-3 px-4 text-white">{challenge.title}</td>
                          <td className="py-3 px-4">{getStatusBadge(challenge.status)}</td>
                          <td className="py-3 px-4 text-gray-300">{challenge.registration_count || 0}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {new Date(challenge.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Challenge Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Create Enterprise Challenge</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Selected Plan: <span className="text-purple-400 font-medium">{pricing[selectedPlan]?.name || 'Starter'}</span>
                    {' • '}{formatPrice(pricing[selectedPlan]?.price || 49999)}
                  </p>
                </div>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateChallenge} className="p-6 space-y-5">
              {/* Challenge Type */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Challenge Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {challengeTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setCreateForm(f => ({ ...f, challenge_type: type.id }))}
                      className={`p-3 rounded-xl border text-sm font-medium flex flex-col items-center gap-2 transition-all ${
                        createForm.challenge_type === type.id
                          ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                          : 'border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                    >
                      <type.icon size={20} />
                      <span className="text-xs">{type.name.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Title */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Challenge Title *</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  placeholder="e.g., CodeVita 2026"
                  required
                />
              </div>
              
              {/* URL Slug */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">/enterprise/your-org/</span>
                  <input
                    type="text"
                    value={createForm.slug}
                    onChange={(e) => setCreateForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    placeholder="codevita-2026"
                  />
                </div>
              </div>
              
              {/* Short Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Short Description</label>
                <input
                  type="text"
                  value={createForm.short_description}
                  onChange={(e) => setCreateForm(f => ({ ...f, short_description: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  placeholder="A brief tagline for your challenge"
                />
              </div>
              
              {/* Full Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Full Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500 resize-none"
                  placeholder="Describe your challenge in detail..."
                />
              </div>
              
              {/* Participation Type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Participation Type</label>
                  <select
                    value={createForm.participation_type}
                    onChange={(e) => setCreateForm(f => ({ ...f, participation_type: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="individual">Individual</option>
                    <option value="team">Team</option>
                  </select>
                </div>
                {createForm.participation_type === 'team' && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Max Team Size</label>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={createForm.max_team_size}
                      onChange={(e) => setCreateForm(f => ({ ...f, max_team_size: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                )}
              </div>
              
              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Registration Start</label>
                  <input
                    type="datetime-local"
                    value={createForm.registration_start}
                    onChange={(e) => setCreateForm(f => ({ ...f, registration_start: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Registration End</label>
                  <input
                    type="datetime-local"
                    value={createForm.registration_end}
                    onChange={(e) => setCreateForm(f => ({ ...f, registration_end: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Challenge Start</label>
                  <input
                    type="datetime-local"
                    value={createForm.challenge_start}
                    onChange={(e) => setCreateForm(f => ({ ...f, challenge_start: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Challenge End</label>
                  <input
                    type="datetime-local"
                    value={createForm.challenge_end}
                    onChange={(e) => setCreateForm(f => ({ ...f, challenge_end: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              
              {/* Plan Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Select Plan</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.values(pricing).filter(p => p.enabled).map(plan => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        selectedPlan === plan.id
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="text-white font-semibold">{plan.name}</div>
                      <div className="text-gray-400 text-sm">{formatPrice(plan.price)}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 bg-white/5 text-gray-300 rounded-xl font-medium hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Challenge
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusModal && statusModalData.challenge && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a25] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden" data-testid="status-confirm-modal">
            <div className={`p-6 ${
              statusModalData.newStatus === 'live' ? 'bg-gradient-to-r from-green-600 to-green-700' :
              'bg-gradient-to-r from-gray-600 to-gray-700'
            }`}>
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-3 rounded-full">
                  {statusModalData.newStatus === 'live' ? <Play size={24} /> : <Pause size={24} />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {statusModalData.newStatus === 'live' ? 'Go Live' : 'End Challenge'}
                  </h2>
                  <p className="text-white/70 text-sm">Confirm this action</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Challenge:</p>
                <p className="font-semibold text-white">{statusModalData.challenge.title}</p>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-sm flex items-start gap-2">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  {statusModalData.newStatus === 'live' 
                    ? 'Going live will make your challenge visible to all participants and open registrations.'
                    : 'Ending this challenge will close registrations and mark it as completed.'}
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusModalData({ challenge: null, newStatus: '' });
                  }}
                  className="flex-1 py-3 bg-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/20"
                  data-testid="status-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateStatus(statusModalData.challenge.id, statusModalData.newStatus)}
                  disabled={updatingStatus}
                  className={`flex-1 py-3 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 ${
                    statusModalData.newStatus === 'live' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                  data-testid="status-confirm-btn"
                >
                  {updatingStatus ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      {statusModalData.newStatus === 'live' ? 'Go Live Now' : 'End Challenge'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareChallenge && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a25] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden" data-testid="share-modal">
            <div className="bg-gradient-to-r from-purple-600 to-cyan-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-full">
                    <Share2 size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Share Challenge</h2>
                    <p className="text-white/70 text-sm">Spread the word</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-white/70 hover:text-white p-2"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Challenge Link:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${getFrontendUrl()}/enterprise/${shareChallenge.org_slug}/${shareChallenge.slug}?ref=share`}
                    className="flex-1 bg-white/5 text-white text-sm px-3 py-2 rounded-lg border border-white/10"
                  />
                  <button
                    onClick={() => copyLink(shareChallenge, 'landing')}
                    className="p-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30"
                  >
                    <Copy size={18} />
                  </button>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Assessment Link (for participants):</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${getFrontendUrl()}/enterprise/${shareChallenge.id}/assessment?ref=share`}
                    className="flex-1 bg-white/5 text-white text-sm px-3 py-2 rounded-lg border border-white/10"
                  />
                  <button
                    onClick={() => copyLink(shareChallenge, 'assessment')}
                    className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30"
                  >
                    <Copy size={18} />
                  </button>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-400 mb-3">Share on social media:</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => shareOnSocial('linkedin')}
                    className="flex-1 py-3 bg-[#0A66C2]/20 text-[#0A66C2] rounded-xl font-medium hover:bg-[#0A66C2]/30 flex items-center justify-center gap-2 border border-[#0A66C2]/30"
                    data-testid="share-linkedin"
                  >
                    <Linkedin size={20} />
                    LinkedIn
                  </button>
                  <button
                    onClick={() => shareOnSocial('twitter')}
                    className="flex-1 py-3 bg-[#1DA1F2]/20 text-[#1DA1F2] rounded-xl font-medium hover:bg-[#1DA1F2]/30 flex items-center justify-center gap-2 border border-[#1DA1F2]/30"
                    data-testid="share-twitter"
                  >
                    <Twitter size={20} />
                    Twitter
                  </button>
                  <button
                    onClick={() => shareOnSocial('whatsapp')}
                    className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] rounded-xl font-medium hover:bg-[#25D366]/30 flex items-center justify-center gap-2 border border-[#25D366]/30"
                    data-testid="share-whatsapp"
                  >
                    <Send size={20} />
                    WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal with Coupon */}
      {showCheckoutModal && checkoutChallenge && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a25] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden" data-testid="checkout-modal">
            <div className="bg-gradient-to-r from-purple-600 to-cyan-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-full">
                    <CreditCard size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Checkout</h2>
                    <p className="text-white/70 text-sm">{checkoutChallenge.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowCheckoutModal(false); setCheckoutChallenge(null); setCouponCode(''); setCouponApplied(null); }}
                  className="text-white/70 hover:text-white p-2"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Plan Summary */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Plan</span>
                  <span className="text-white font-medium flex items-center gap-2">
                    <Crown className="text-amber-400" size={16} />
                    {checkoutChallenge.plan_details?.name || 'Starter'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Original Price</span>
                  <span className={`${couponApplied ? 'line-through text-gray-500' : 'text-white font-bold text-xl'}`}>
                    ₹{(checkoutChallenge.plan_details?.price || 49999).toLocaleString()}
                  </span>
                </div>
                {couponApplied && (
                  <>
                    <div className="flex items-center justify-between mt-2 text-green-400">
                      <span>Discount ({couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}%` : `₹${couponApplied.discount_value}`})</span>
                      <span>
                        -₹{((checkoutChallenge.plan_details?.price || 49999) - calculateDiscountedPrice()).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between">
                      <span className="text-white font-semibold">Final Price</span>
                      <span className="text-2xl font-bold text-green-400">
                        {calculateDiscountedPrice() === 0 ? 'FREE' : `₹${calculateDiscountedPrice().toLocaleString()}`}
                      </span>
                    </div>
                  </>
                )}
              </div>
              
              {/* Coupon Input */}
              <div>
                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <Tag size={14} />
                  Have a coupon code?
                </label>
                {!couponApplied ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white uppercase focus:outline-none focus:border-purple-500"
                      placeholder="Enter coupon code"
                      data-testid="coupon-input"
                    />
                    <button
                      onClick={handleValidateCoupon}
                      disabled={couponValidating || !couponCode.trim()}
                      className="px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      data-testid="apply-coupon-btn"
                    >
                      {couponValidating ? <Loader2 className="animate-spin" size={18} /> : <Percent size={18} />}
                      Apply
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-400" size={18} />
                      <span className="text-green-400 font-medium">{couponApplied.code}</span>
                      <span className="text-green-400/70 text-sm">
                        ({couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}% off` : `₹${couponApplied.discount_value} off`})
                      </span>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-gray-400 hover:text-red-400 p-1"
                      data-testid="remove-coupon-btn"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowCheckoutModal(false); setCheckoutChallenge(null); setCouponCode(''); setCouponApplied(null); }}
                  className="flex-1 py-3 bg-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/20"
                >
                  Cancel
                </button>
                <button
                  onClick={processPayment}
                  disabled={processingPayment}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="proceed-payment-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} />
                      {calculateDiscountedPrice() === 0 ? 'Complete for Free' : `Pay ₹${calculateDiscountedPrice().toLocaleString()}`}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyEnterpriseHostingPage;
